import { IReactiveModel, ViewModel } from '@descomplica/microfrontend-core';
import { action, makeObservable, observable } from 'mobx';

import { GetAnswerByGuidCollaboration } from '@/application/collaborations/exercises/get-answer-by-guid.collaboration';
import { GetExamAnswerByGuidCollaboration } from '@/application/collaborations/exercises/get-exam-answer-by-guid.collaboration';
import { GetExamByGuidCollaboration } from '@/application/collaborations/exercises/get-exam-by-guid.collaboration';
import { GetExamExercisesCollaboration } from '@/application/collaborations/exercises/get-exam-exercises.collaboration';
import { PostExamAnswerByGuidCollaboration } from '@/application/collaborations/exercises/post-exam-answer-by-guid.collaboration';
import { SetExamFinishedCollaboration } from '@/application/collaborations/exercises/set-exam-finished.collaboration';
import type { AnswerDto } from '@/application/interfaces/exercises/pegasus.gateway';
import { ClassifierDto } from '@/application/interfaces/pegasus.gateway';
import { Tracking } from '@/application/utils/tracking';
import { ApplicationModel } from '@/presentation/models/application.model';
import { StudentClassesStore } from '@/presentation/stores/studentClasses.store';

type Answer = {
  exercises_guid: string;
  answers: {
    question_guid: string;
    choices_guid: string;
  }[];
};
@ViewModel({ singleton: true })
export class ExamViewModel implements IReactiveModel {
  @observable isLoading = true;
  @observable classifiers: ClassifierDto[] = [];
  @observable highestClassificationLevels: ClassifierDto[] = [];
  @observable answeredExercises = [];
  @observable exercises;
  @observable answered = false;
  @observable exercisesGuid: string;
  @observable currentStep = 0;
  @observable currentQuestion;
  @observable selectedOption = null;
  @observable showResult = false;
  @observable answers: Answer;
  @observable examExercises;

  constructor(
    private readonly applicationModel: ApplicationModel,
    private getExamByGuidCollaboration: GetExamByGuidCollaboration,
    private getExamAnswerByGuidCollaboration: GetExamAnswerByGuidCollaboration,
    private getAnswerByGuidCollaboration: GetAnswerByGuidCollaboration,
    private getExamExercisesCollaboration: GetExamExercisesCollaboration,
    private postExamAnswerByGuidCollaboration: PostExamAnswerByGuidCollaboration,
    private setExamFinishedCollaboration: SetExamFinishedCollaboration,
    private readonly studentClassesStore: StudentClassesStore,
  ) {
    makeObservable(this);
  }

  @action
  async onInit() {
    this.isLoading = false;
    Tracking.pageView(this.studentClassesStore.getStudentClassesTrackingInfo());
  }

  @action
  setClassifiers(classifiers: ClassifierDto[]) {
    this.classifiers = classifiers;
  }

  @action
  setCurrentStep(step: number) {
    this.currentStep = step;
  }

  @action.bound
  setCurrentQuestion(question: any) {
    this.currentQuestion = question;
  }

  @action.bound
  setExercisesGuid(guid: string) {
    this.exercisesGuid = guid;
  }

  @action
  setShowResult(result: boolean) {
    this.showResult = result;
  }

  @action.bound
  async setFinishedExam(guid: string) {
    await this.setExamFinishedCollaboration.run(guid);
  }

  @action.bound
  setSelectedOption(selectedOption: string | null) {
    this.selectedOption = selectedOption;
  }

  get application() {
    return this.applicationModel.application;
  }

  @action
  async handleScreenEnter(guid: string): Promise<void> {
    this.isLoading = true;
    this.answeredExercises = [];
    try {
      this.exercisesGuid = guid;

      const { data: exercises } = await this.getExamByGuidCollaboration.run(guid);

      this.exercises = exercises;

      if (this.exercises && this.exercises.exercisesQuestion.length > 0) {
        const { data: answerExercises } = await this.getExamAnswerByGuidCollaboration.run({
          guid,
        });

        if (answerExercises && answerExercises.answers.length > 0) {
          const questions = this.exercises.exercisesQuestion.map((exercise) => {
            answerExercises.answers.map((answer) => {
              if (answer.question_guid === exercise.question_guid) {
                exercise.answered = true;
                exercise.correct = answer.correct;
                exercise.choices_guid = answer.choices_guid;
                exercise.correct_choice = answer.correct_choice;

                this.answeredExercises.push({
                  choices_guid: exercise.choices_guid,
                  question_guid: exercise.question_guid,
                });
              }
            });

            return exercise;
          });

          this.exercises = { ...this.exercises, exercisesQuestion: questions };
        }
      }

      if (this.answeredExercises.length === this.exercises.exercisesQuestion.length) {
        const { data: examExercises } = await this.getExamExercisesCollaboration.run(guid);
        this.examExercises = examExercises;

        if (this.examExercises) {
          await this.setExamFinishedCollaboration.run(this.examExercises.guid);
          this.setShowResult(true);
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      this.isLoading = false;
    }
  }

  @action
  async getAnswerQuestion(dto: AnswerDto) {
    try {
      await this.getAnswerByGuidCollaboration.run(dto);
      await this.handleScreenEnter(this.exercisesGuid);
    } catch (error) {
      console.error(error);
    }
  }

  @action
  async postExamAnswerQuestion() {
    try {
      await this.postExamAnswerByGuidCollaboration.run(this.answers);

      const { data: examExercises } = await this.getExamExercisesCollaboration.run(
        this.exercisesGuid,
      );
      this.examExercises = examExercises;

      if (this.examExercises) {
        await this.setFinishedExam(this.examExercises.guid);
      }

      await this.handleScreenEnter(this.exercisesGuid);
    } catch (error) {
      console.error(error);
    }
  }

  @action
  async pushAnswersArray(answer: Answer, guid: string) {
    if (!this.answers) {
      this.answers = {
        exercises_guid: guid,
        answers: [],
      };
    }

    const existingAnswerIndex = this.answers.answers.findIndex((item) => {
      return item.question_guid === answer.answers[0].question_guid;
    });

    if (existingAnswerIndex === -1) {
      this.answers.answers.push(answer.answers[0]);
    } else {
      this.answers.answers[existingAnswerIndex].choices_guid = answer.answers[0].choices_guid;
    }
  }
}
